// NATIVE
import React, { useState, useEffect } from 'react';

// INSTALLED
import { Link, useNavigate, useLocation } from 'react-router-dom';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useDispatch, useSelector } from 'react-redux';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ListItemButton from '@mui/material/ListItemButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styled, Theme, CSSObject, alpha } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

// CUSTOM
import { Box } from '@mui/material';
import { theme } from '../../commons/theme';
import { closeDrawerAction } from '../../store/globals';
import { IconByString } from '../../components/IconByString';
import { LogoPlatform } from '../../components/LogoPlatform';
import { getIndexParentMenu } from '../../commons/utils';
import { types } from '../../commons/types/types';
import { NovedadesDrawer } from './components/NovedadesDrawer';
import { SystemVersion } from './components/SystemVersion';

const drawerWidth = 320;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: '#f8f9fa',
  borderRight: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '4px 0 24px rgba(0, 0, 0, 0.02)',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  backgroundColor: '#f8f9fa',
  borderRight: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '4px 0 24px rgba(0, 0, 0, 0.02)',
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

// Recursive helper to check if an item or any of its children match the current route
const isItemActive = (item: any, currentPath: string): boolean => {
  if (item.to && item.to !== '' && currentPath.startsWith(item.to)) {
    return true;
  }
  if (item.childrens && item.childrens.length > 0) {
    return item.childrens.some((child: any) => isItemActive(child, currentPath));
  }
  return false;
};

const RecursiveSidebarItem = ({ item, level = 0, open, theme }: any) => {
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  // Auto-expand if the current route is within this item's children
  useEffect(() => {
    if (item.childrens && item.childrens.length > 0) {
      if (isItemActive(item, location.pathname)) {
        setExpanded(true);
      }
    }
  }, [location.pathname, item]);

  const hasChildren = item.childrens && item.childrens.length > 0;

  const getPaddingLeft = () => {
    if (!open) return 1.5;
    if (level === 0) return 2.5;
    if (level === 1) return 2.5;
    return 3 + (level - 2) * 1;
  };

  const getPaddingRight = () => {
    if (!open) return 1.5;
    if (level === 0) return 2.5;
    return 2;
  };

  const getMinHeight = () => {
    if (level === 0) return 44;
    if (level === 1) return 40;
    return 36;
  };

  const getBorderRadius = () => {
    if (level === 0) return '12px';
    if (level === 1) return '10px';
    return '8px';
  };

  const normalizePath = (path: string) =>
    path.replace(/\/+$|^\//g, (match) => (match === '/' ? '/' : ''));
  const currentPath = normalizePath(location.pathname);
  const targetPath = normalizePath(item.to ?? '');
  const isActive = targetPath
    ? targetPath === '/'
      ? currentPath === '/'
      : currentPath.startsWith(targetPath)
    : false;

  // Determine selection state for ListItemButton
  const isSelected = isActive;

  // Toggle expansion for items with children
  const handleToggle = () => {
    if (hasChildren) {
      setExpanded((prev) => !prev);
    }
  };

  React.useEffect(() => {
    if (isActive) {
      setExpanded(true);
    }
  }, [isActive]);

  const buttonSx = {
    minHeight: getMinHeight(),
    justifyContent: open ? 'initial' : 'center',
    pl: getPaddingLeft(),
    pr: getPaddingRight(),
    margin: level === 0 ? '4px 8px' : '2px 8px',
    borderRadius: getBorderRadius(),
    transition: 'all 0.2s ease',
    ...(hasChildren
      ? {
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, level === 0 ? 0.12 : 0.08),
            color: theme.palette.primary.main,
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.main,
            },
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, level === 0 ? 0.06 : 0.04),
          },
        }
      : {
          '&.Mui-selected': {
            backgroundColor: '#765feb',
            '& .MuiListItemIcon-root': {
              color: theme.palette.common.white,
            },
            '& .MuiListItemText-primary': {
              color: theme.palette.common.white,
              fontWeight: 700,
            },
          },
          '&.Mui-selected:hover': {
            backgroundColor: '#634bde',
          },
          '&:hover': {
            backgroundColor: 'rgba(118, 95, 235, 0.08)',
          },
        }),
  };

  return (
    <Box>
      <ListItemButton
        onClick={handleToggle}
        selected={isSelected && !hasChildren}
        component={hasChildren ? 'div' : Link}
        to={hasChildren ? undefined : item.to}
        sx={buttonSx}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 2 : 'auto',
            justifyContent: 'center',
            color:
              isSelected && !hasChildren ? theme.palette.common.white : theme.palette.text.primary,
          }}
        >
          <IconByString iconName={item.icon} />
        </ListItemIcon>
        <ListItemText
          sx={{
            opacity: open ? 1 : 0,
            display: open ? 'block' : 'none',
            textTransform: 'capitalize',
            '& .MuiListItemText-primary': {
              fontWeight: isSelected ? (level === 0 ? 700 : level === 1 ? 600 : 700) : 500,
              fontSize: level === 0 ? '0.95rem' : level === 1 ? '0.9rem' : '0.85rem',
              color: isSelected
                ? hasChildren
                  ? theme.palette.primary.main
                  : theme.palette.common.white
                : theme.palette.text.primary,
              whiteSpace: 'break-spaces',
              lineHeight: 1.2,
            },
          }}
          primary={item.text}
          secondary={item.subText}
          secondaryTypographyProps={{
            sx: {
              fontSize: '0.7rem',
              lineHeight: 1.2,
              mt: 0.5,
              textTransform: 'none',
              color:
                isSelected && !hasChildren
                  ? alpha(theme.palette.common.white, 0.7)
                  : 'text.secondary',
              whiteSpace: 'break-spaces',
            },
          }}
        />
        {hasChildren &&
          open &&
          (expanded ? (
            <ExpandLess sx={{ color: theme.palette.text.primary }} />
          ) : (
            <ExpandMore sx={{ color: theme.palette.text.primary }} />
          ))}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              ...(open && {
                mx: 1.5,
                mb: 1,
                mt: 0.5,
                py: 0.5,
                bgcolor: alpha(theme.palette.primary.main, level === 0 ? 0.03 : 0.05),
                borderRadius: getBorderRadius(),
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, level === 0 ? 0.06 : 0.08),
                position: 'relative',
                ...(level === 0
                  ? {
                      boxShadow: 'inset 0px 2px 4px rgba(0,0,0,0.02)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 10,
                        bottom: 10,
                        width: '4px',
                        bgcolor: theme.palette.primary.main,
                        borderRadius: '0 4px 4px 0',
                        opacity: 0.5,
                      },
                    }
                  : {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 10,
                        bottom: 10,
                        width: '3px',
                        bgcolor: theme.palette.primary.main,
                        borderRadius: '0 4px 4px 0',
                        opacity: 0.8,
                      },
                    }),
              }),
            }}
          >
            {item.childrens
              .filter((child: any) => child.visible === undefined || child.visible === true)
              .map((child: any, idx: number) => (
                <RecursiveSidebarItem
                  key={`child-${level}-${idx}-${child.text}`}
                  item={child}
                  level={level + 1}
                  open={open}
                  theme={theme}
                />
              ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};

export const MenuSidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasUpdates, setHasUpdates] = useState(false);
  const [newsUpdated, setNewsUpdated] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const newsData = window.localStorage.getItem(types.storage.newsUpdatedStorage);
    const hasUpdatesFlag = window.localStorage.getItem(types.storage.hasUpdatesStorage);

    setHasUpdates(!!newsData);
    if (newsData) {
      try {
        setNewsUpdated(JSON.parse(newsData));
        if (hasUpdatesFlag === 'true') {
          setIsDrawerOpen(true);
        }
      } catch (error) {}
    }
  }, []);

  const handleViewNovedades = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseNovedades = () => {
    setIsDrawerOpen(false);
  };

  // REDUX
  const { drawer }: any = useSelector((state: any) => state.globals);
  const { user_permissions }: any = useSelector((state: any) => state.permissions);

  const [open, setOpen] = useState(false);

  const [menuSideBar, setMenuSideBar] = useState<any[]>([]);

  useEffect(() => {
    setOpen(drawer);
  }, [drawer]);

  useEffect(() => {
    const permissions = user_permissions || [];
    setMenuSideBar(permissions);
  }, [user_permissions]);

  const handleDrawerClose = async () => {
    await dispatch(closeDrawerAction());
  };

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader
        sx={{
          justifyContent: open ? 'space-between' : 'center',
          px: open ? 2 : 1,
        }}
      >
        <LogoPlatform />
        {open && (
          <>
            <Box
              component={Link}
              to="/"
              sx={{
                color: theme.palette.text.primary,
                fontSize: '1.15rem',
                lineHeight: '1',
                fontWeight: '800',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                textDecoration: 'none',
                mr: 'auto',
                ml: 1.5,
              }}
            >
              Qihotels
            </Box>
            <IconButton sx={{ color: theme.palette.text.primary }} onClick={handleDrawerClose}>
              {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </>
        )}
      </DrawerHeader>

      <List sx={{ px: 0 }}>
        {menuSideBar.length > 0 &&
          menuSideBar
            .filter((item: any) => item.visible === undefined || item.visible === true)
            .map((item: any, indexf: any) => (
              <RecursiveSidebarItem
                key={`root-${indexf}-${item.text}`}
                item={item}
                level={0}
                open={open}
                theme={theme}
              />
            ))}
      </List>

      {open && hasUpdates && (
        <SystemVersion newsUpdated={newsUpdated} handleViewNovedades={handleViewNovedades} />
      )}

      <NovedadesDrawer
        open={isDrawerOpen}
        onClose={handleCloseNovedades}
        newsUpdated={newsUpdated}
      />
    </Drawer>
  );
};
